import type { MetricRenderAdapter } from "@/metrics/runtime-adapters"
import type { RunOutlier, RunOutlierMetricSample } from "@/runs"

import {
  buildOutlierEvidence,
  type OutlierEvidenceBySimulationId,
} from "./buildOutlierEvidence"

interface OutlierMetricOption {
  id: string
  label: string
}

interface SelectedOutliers {
  availableMetricOptions: OutlierMetricOption[]
  hasExplicitNoSelection: boolean
  hasLowSampleWarning: boolean
  hasNoUsableMetricData: boolean
  outlierEvidenceBySimulationId: OutlierEvidenceBySimulationId
  qualifyingMetricIdsBySimulationId: Map<string, Set<string>>
  qualifyingSimulationOutputIds: Set<string>
}

const MIN_RELIABLE_OUTLIER_SAMPLE_SIZE = 12

function getMetricVersionKey(
  value: Pick<
    RunOutlier | RunOutlierMetricSample,
    "metric_id" | "metric_version_id" | "metric_version_number"
  >,
): string {
  return JSON.stringify([
    value.metric_id,
    value.metric_version_id ?? null,
    value.metric_version_number ?? null,
  ])
}

export function shapeSelectedOutliers({
  metricRenderAdaptersById,
  metricSamples,
  outliers,
  selectedMetricIds,
}: {
  metricRenderAdaptersById: Record<string, MetricRenderAdapter>
  metricSamples: readonly RunOutlierMetricSample[]
  outliers: readonly RunOutlier[]
  selectedMetricIds: readonly string[] | null
}): SelectedOutliers {
  const labelsByMetricId = new Map(
    Object.values(metricRenderAdaptersById).map((metric) => [
      metric.id,
      metric.metric_display_name,
    ]),
  )
  const availableMetricIds = [
    ...new Set(metricSamples.map((sample) => sample.metric_id)),
  ]
  const availableMetricIdSet = new Set(availableMetricIds)
  const effectiveSelectedMetricIds = new Set(
    (selectedMetricIds ?? availableMetricIds).filter((id) =>
      availableMetricIdSet.has(id),
    ),
  )
  const selectedSamples = metricSamples.filter((sample) =>
    effectiveSelectedMetricIds.has(sample.metric_id),
  )
  const usableSamplesByVersion = new Map(
    selectedSamples
      .filter(
        (sample) =>
          sample.status === "analyzed" &&
          typeof sample.iqr === "number" &&
          sample.iqr > 0,
      )
      .map((sample) => [getMetricVersionKey(sample), sample]),
  )
  const qualifyingOutliers = outliers.filter(
    (outlier) =>
      outlier.severity === "extreme" &&
      effectiveSelectedMetricIds.has(outlier.metric_id) &&
      usableSamplesByVersion.has(getMetricVersionKey(outlier)),
  )
  const hasExplicitNoSelection =
    selectedMetricIds !== null && effectiveSelectedMetricIds.size === 0
  const qualifyingSimulationOutputIds = new Set<string>()
  const qualifyingMetricIdsBySimulationId = new Map<string, Set<string>>()
  for (const outlier of qualifyingOutliers) {
    qualifyingSimulationOutputIds.add(outlier.simulation_output_id)
    const metricIds = qualifyingMetricIdsBySimulationId.get(
      outlier.simulation_output_id,
    )
    if (metricIds) {
      metricIds.add(outlier.metric_id)
    } else {
      qualifyingMetricIdsBySimulationId.set(
        outlier.simulation_output_id,
        new Set([outlier.metric_id]),
      )
    }
  }

  return {
    availableMetricOptions: availableMetricIds
      .map((id) => ({ id, label: labelsByMetricId.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    hasExplicitNoSelection,
    hasLowSampleWarning: selectedSamples.some(
      (sample) => sample.sample_size < MIN_RELIABLE_OUTLIER_SAMPLE_SIZE,
    ),
    hasNoUsableMetricData:
      !hasExplicitNoSelection && usableSamplesByVersion.size === 0,
    outlierEvidenceBySimulationId: buildOutlierEvidence(
      qualifyingOutliers,
      metricSamples,
      metricRenderAdaptersById,
    ),
    qualifyingMetricIdsBySimulationId,
    qualifyingSimulationOutputIds,
  }
}
